import React, { useMemo, useState } from 'react';

import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';
import {
  RowSelectionState,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { GenerateQuestionsFromSpecificStudentsAdapter } from '~analytics/adapters/GenerateQuestionsFromSpecificStudentsAdapter';
import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons/SmallButton';
import { IconExclamationTriangle } from '~shared/components/icons/large';
import { SelectNumberRadioGroup } from '~shared/components/SelectNumberRadio/SelectNumberRadio';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2/TooltipV2';
import { useOpenPaywallDialogSpecificStudents } from '~teacher/components/PaywallDialog/PaywallDialogSpecificStudents';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { GenerateQuestionDialogContent } from '~teacher/components/ResultsGrid/Insights/GenerateQuestions/Layout/GenerateQuestionDialogContent';
import { proCheck } from '~teacher/util/proCheck';

import { SpecificStudentAreas } from './SpecificStudentAreas';
import { SpecificStudentScoreCell } from './SpecificStudentScoreCell';

export interface Area {
  index: number;
  name: string;
  description: string;
  isImprovementNeeded: boolean;
}

export interface StudentRow {
  id: number;
  name: string;
  areas: Area[];
  score: {
    currentScore: number;
    maxScore: number;
    isAbsent: boolean;
    percentage?: number;
  };
  // Raw score and Absent flag for sorting
  _rawScore: number;
  _isAbsent: boolean;
}

export type OnGenerate = (params: {
  selectedStudentIds: number[];
  questionCount: number;
  generateExplanations: boolean;
  numAreasOfImprovement: number;
}) => void;

interface SelectStudentsProps {
  backToGenerationType?: () => void;
  students: StudentRow[];
  onGenerate: OnGenerate;
  isGenerating?: boolean;
  isInsightsOutdated?: boolean;
}

const columnHelper = createColumnHelper<StudentRow>();

function getAoIIndexes(studentList: StudentRow[]) {
  return new Set(
    studentList.flatMap((s) =>
      s.areas.filter((a) => a.isImprovementNeeded).map((a) => a.index)
    )
  );
}

export function SelectStudents({
  backToGenerationType,
  students,
  onGenerate,
  isGenerating,
  isInsightsOutdated = false,
}: SelectStudentsProps) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [questionCount, setQuestionCount] = useState(5);
  const [generateExplanations, setGenerateExplanations] = useState(true);
  const openSpecificStudentsPaywall = useOpenPaywallDialogSpecificStudents();

  // Derive selected student IDs from row selection state
  const selectedStudentIds = useMemo(() => {
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => students[parseInt(key, 10)]?.id)
      .filter((id): id is number => id !== undefined);
  }, [rowSelection, students]);

  const selectedStudents = students.filter((s) =>
    selectedStudentIds.includes(s.id)
  );

  const selectedAoIIndexes = getAoIIndexes(selectedStudents);
  const totalAoIIndexes = getAoIIndexes(students);

  // Default sorting: Absent first (desc), then low score to high (asc)
  const [sorting] = useState<SortingState>([
    { id: '_isAbsent', desc: true },
    { id: '_rawScore', desc: false },
  ]);

  const columns = [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center justify-center">
          <SlCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onSlChange={(e) =>
              table.toggleAllRowsSelected(
                (e.target as unknown as HTMLInputElement).checked
              )
            }
            aria-label={translate('Select all students')}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <SlCheckbox
            checked={row.getIsSelected()}
            onSlChange={(e) =>
              row.toggleSelected(
                (e.target as unknown as HTMLInputElement).checked
              )
            }
          >
            <span className="sr-only">
              {translate('Select student {0}').format(row.original.name)}
            </span>
          </SlCheckbox>
        </div>
      ),
      size: 48,
    }),
    columnHelper.accessor('name', {
      header: () => translate('Name'),
      cell: (info) => (
        <div className="max-w-56">
          <span className="text-base text-blue-900 leading-8 truncate block">
            {info.getValue()}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('areas', {
      header: () => (
        <div className="flex items-center gap-1">
          <span className="text-xs font-700 text-grey-900">
            {translate('Areas')}
          </span>
          {isInsightsOutdated && (
            <TooltipV2
              label={translate(
                'Your insights may be out of date. Reanalyze to include the latest grades.'
              )}
              contentWidth={260}
              mode="click"
            >
              <div className="inline-flex cursor-pointer">
                <IconExclamationTriangle className="text-amber-700 h-5 w-5" />
              </div>
            </TooltipV2>
          )}
        </div>
      ),
      cell: (info) => <SpecificStudentAreas areas={info.getValue()} />,
    }),
    columnHelper.accessor('score', {
      header: () => translate('Score'),
      cell: (info) => <SpecificStudentScoreCell score={info.getValue()} />,
    }),
    // Hidden columns used for sorting
    columnHelper.accessor('_isAbsent', {
      header: () => null,
      cell: () => null,
      enableHiding: true,
    }),
    columnHelper.accessor('_rawScore', {
      header: () => null,
      cell: () => null,
      enableHiding: true,
    }),
  ];

  const sendSelectedStudentsAnalytics = () => {
    new GenerateQuestionsFromSpecificStudentsAdapter({
      num_students: students.length,
      num_students_selected: selectedStudentIds.length,
      num_aoi_selected_from_students: `${selectedAoIIndexes.size}/${totalAoIIndexes.size}`,
      num_questions_to_generate: questionCount,
      generate_explanations: generateExplanations,
    }).sendAnalytics();
  };

  const handleGenerate = () => {
    if (selectedStudentIds.length === 0 || isGenerating) return;

    /*
     * Send analytics event to amplitude when user clicks on generate button
     * regardless of whether the user has pro or not
     **/
    sendSelectedStudentsAnalytics();

    if (!proCheck.isPro()) {
      openSpecificStudentsPaywall().then();
      return;
    }

    onGenerate({
      selectedStudentIds,
      questionCount,
      generateExplanations,
      numAreasOfImprovement: selectedAoIIndexes.size,
    });
  };

  const table = useReactTable({
    data: students,
    columns,
    state: {
      rowSelection,
      sorting,
      columnVisibility: {
        _isAbsent: false,
        _rawScore: false,
      },
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <GenerateQuestionDialogContent
      header={
        <GenerateQuestionDialogContent.Header
          title={translate('Generate from Specific Students')}
          description={translate(
            'Generate questions that target the improvement areas found across the selected students.'
          )}
          onBack={backToGenerationType}
          isPro={proCheck.isPro()}
        />
      }
      footer={
        <GenerateQuestionDialogContent.Footer>
          <div className="flex justify-between items-center gap-2 p-5">
            <div className="flex flex-row items-center gap-2">
              <label
                className="text-base text-blue-900"
                htmlFor="generate-explanations-toggle"
                id="generate-explanations-label"
              >
                {translate('Generate Explanations')}{' '}
              </label>

              <TooltipV2
                mode="click"
                label={translate(
                  'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
                )}
                contentWidth={260}
              >
                <InformationIcon />
              </TooltipV2>
            </div>

            <ToggleSwitch
              id="generate-explanations-toggle"
              size="small"
              on={generateExplanations}
              onChange={(value) => setGenerateExplanations(value)}
            />
          </div>
          <div className="p-5 flex flex-col gap-4 shadow-inset-border-t">
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-row items-center gap-2">
                <label
                  htmlFor="number-of-questions"
                  className="text-base text-blue-900 leading-8"
                  id="number-of-questions-label"
                >
                  {translate('Number of Questions')}
                </label>
                <TooltipV2
                  mode="click"
                  label={translate(
                    'All areas covered, giving more questions to the biggest gaps.'
                  )}
                  contentWidth={260}
                >
                  <InformationIcon />
                </TooltipV2>
              </div>
              <SelectNumberRadioGroup
                id="number-of-questions"
                aria-labelledby="number-of-questions-label"
                name="numberOfQuestions"
                selectedNumber={questionCount}
                numberOptions={[5, 10, 15]}
                onChange={setQuestionCount}
              >
                <span slot="label" className="sr-only"></span>
              </SelectNumberRadioGroup>
            </div>
          </div>
          <div className="flex justify-end p-5 shadow-inset-border-t">
            <SmallButton
              appearance="primary"
              isDisabled={selectedStudentIds.length === 0}
              isLoading={isGenerating}
              onClick={handleGenerate}
            >
              {translate('Generate')}
            </SmallButton>
          </div>
        </GenerateQuestionDialogContent.Footer>
      }
    >
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-solid border-0 border-b border-grey-200"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`pt-8 pb-4 text-left uppercase ${
                        header.id === 'select'
                          ? 'w-12 px-2'
                          : header.id === 'areas'
                          ? 'px-0'
                          : 'px-4'
                      }`}
                    >
                      <span className="text-xs font-700 text-grey-900 uppercase">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-grey-100 hover:bg-grey-50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`py-3 ${
                        cell.column.id === 'select'
                          ? 'w-12 px-2'
                          : cell.column.id === 'areas'
                          ? 'px-0'
                          : 'px-4'
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </GenerateQuestionDialogContent>
  );
}
